import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  shouldShowLeaderboardInterstitial,
  LeaderboardInterstitialRow
} from '../../../../../../../shared/frontend/components/ads/leaderboardInterstitial';
import { FeatureFlag } from '../../../../../../../shared/gameConfig/sharedTypes';
import { SEASONS_BY_EXACT_SLUGS } from '../../../../../shared/enums';
import { TKeystoneRunRankingsTableProps } from './types';
import { KeystoneRunRow } from './KeystoneRunRow';

export function KeystoneRunRankingsTableLarge(props: TKeystoneRunRankingsTableProps) {
  const season = SEASONS_BY_EXACT_SLUGS[props.season] || {};
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (isOverflowing) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;

    if (containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        setIsOverflowing(
          (isOverflowing) => isOverflowing || containerRef.current!.scrollWidth > containerRef.current!.clientWidth
        );
      });

      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const cols = useMemo(() => {
    const result = props.columns
      ? { ...props.columns }
      : {
          showRun: true,
          rank: true,
          dungeon: !props.dungeon.name,
          level: true,
          time: true,
          affixes: true,
          ...(!props.siteConfig.isEmbedded
            ? {
                tank: true,
                healer: true,
                dps: true,
                faction: season.supportsFactions || false,
                route: season.supportsRouteGeneration || false,
                replay: props.currentFeatures[FeatureFlag.DOWNLOAD_REPLAYS] && (season.supportsReplays || false)
              }
            : {
                // when embedded we need to hide the timer
                time: false,
                group: true
              }),
          score: true,
          video: true,
          platoonAssigned: false
        };

    const hasAnyVideos = !!props.groups.find((g: any) => g.run.videos?.length > 0);

    if (!hasAnyVideos) {
      result.video = false;
    }

    const isAnyRanked = props.groups.some((group: any) => group.rank > 0);

    if (!isAnyRanked) {
      result.rank = false;
      result.runLink = true;
    }

    return result;
  }, [props.columns, props.dungeon, props.siteConfig.isEmbedded, props.currentFeatures, season, props.groups]);

  const numColumns = Object.values(cols).filter((v) => v === true).length;

  const Container = props.wrapMembersOnOverflow ? 'div' : Fragment;

  return (
    <Container
      {...(props.wrapMembersOnOverflow ? { style: { maxWidth: '100%', overflow: 'auto' }, ref: containerRef } : {})}
    >
      <table className={cx('slds-table', 'slds-table--bordered', 'slds-table--striped', 'rio-rankings-table')}>
        <thead>
          <tr className="aslds-text-title--caps">
            {cols.showRun && (
              <th style={{ width: '1rem' }} className="slds-text-align--center text-white slds-text-heading--small" />
            )}
            {cols.runLink && (
              <th style={{ width: '7rem' }} className="slds-text-align--center text-white slds-text-heading--small" />
            )}
            {cols.rank && (
              <th style={{ width: '7rem' }} className="slds-text-align--center text-white slds-text-heading--small">
                {t('tables:headers.rank')}
              </th>
            )}
            {cols.tournamentRegion && (
              <th style={{ width: '8rem' }} className="slds-text-align--center text-white slds-text-heading--small">
                {t('tables:headers.region')}
              </th>
            )}
            {cols.dungeon && (
              <th style={{ width: '6rem' }} className="slds-text-align--center text-white slds-text-heading--small">
                {t('tables:headers.dungeon')}
              </th>
            )}
            {cols.level && (
              <th
                style={{ width: '3rem', maxWidth: '3rem' }}
                className="slds-text-align--left text-white slds-text-heading--small"
              >
                {t('tables:headers.level')}
              </th>
            )}
            {cols.time && (
              <th style={{ width: '8rem' }} className="slds-text-align--center text-white slds-text-heading--small">
                {t('tables:headers.time')}
              </th>
            )}
            {cols.affixes && (
              <th
                style={{ width: '7rem', minWidth: '7rem' }}
                className="slds-text-align--center text-white slds-text-heading--small"
              >
                {t('tables:headers.affixes')}
              </th>
            )}
            {cols.tank && (
              <th className="slds-text-align--left text-white slds-text-heading--small">
                {t('common:roles.tank.name')}
                <span className={'slds-m-left--x-small slds-avatar icon-role icon-small tank-lfg'} />
              </th>
            )}
            {cols.healer && (
              <th className="slds-text-align--left text-white slds-text-heading--small">
                {t('common:roles.healer.name')}
                <span className={'slds-m-left--x-small slds-avatar icon-role icon-small healer-lfg'} />
              </th>
            )}
            {cols.dps && (
              <th className="slds-text-align--left text-white slds-text-heading--small">
                {t('common:roles.dps.name')}
                <span className={'slds-m-left--x-small slds-avatar icon-role icon-small dps-lfg'} />
              </th>
            )}
            {cols.group && <th className="slds-text-align--left text-white slds-text-heading--small">Group</th>}
            {cols.score && (
              <th style={{ width: '6rem' }} className="slds-text-align--right text-white slds-text-heading--small">
                {t('tables:headers.score')}
              </th>
            )}
            {cols.faction && <th style={{ width: 1 }} className="slds-text-align--center" />}
            {!props.siteConfig.isEmbedded && cols.video && (
              <th style={{ width: 1 }} className="slds-text-align--center" />
            )}
            {!props.siteConfig.isEmbedded && cols.route && (
              <th style={{ width: 1 }} className="slds-text-align--center" />
            )}
            {!props.siteConfig.isEmbedded && cols.replay && (
              <th style={{ width: 1 }} className="slds-text-align--center" />
            )}
            {cols.platoonAssigned && <th style={{ width: 1 }} className="slds-text-align--center" />}
          </tr>
        </thead>
        <tbody>
          {props.groups.length === 0 && (
            <tr>
              <td colSpan={numColumns}>
                <div className="slds-align--absolute-center" style={{ minHeight: '250px', whiteSpace: 'normal' }}>
                  <div className="slds-text-align--center">
                    <p className="slds-text-heading--medium">{t('common:errors.empty-run-leaderboard')}</p>
                  </div>
                </div>
              </td>
            </tr>
          )}

          {props.groups.map((group: any, index: number) => (
            <Fragment key={group.run.keystone_run_id}>
              {(() => {
                const slotIndex = shouldShowLeaderboardInterstitial(
                  index,
                  props.groups.length,
                  props.currentFeatures,
                  props.siteConfig
                );
                return slotIndex !== false ? (
                  <LeaderboardInterstitialRow
                    slotIndex={slotIndex}
                    seed={props.siteConfig.seed || 0}
                    colSpan={numColumns}
                  />
                ) : null;
              })()}

              <KeystoneRunRow
                key={group.rank}
                group={group}
                cols={cols}
                isEmbedded={props.siteConfig.isEmbedded}
                isOpen={group.run.keystone_run_id.toString() === props.currentRunId}
                teamDetailsRoute={props.teamDetailsRoute}
                isMdiManager={props.isMdiManager}
                platoonNamespace={props.eventNamespace || props.platoonNamespace}
                expandableCharacterRow={props.expandableCharacterRow}
                bracketId={props.bracketId}
                eventNamespace={props.eventNamespace}
                allowGroupMembersWrap={isOverflowing}
              />
            </Fragment>
          ))}
        </tbody>
      </table>
    </Container>
  );
}
